// ──────────────────────────────────────────────────────────────────
// ShopPage.jsx
// ──────────────────────────────────────────────────────────────────
import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import GemCard from '../../components/public/GemCard';
import api from '../../utils/api';

export function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [gems, setGems]       = useState([]);
  const [categories, setCats] = useState([]);
  const [pagination, setPag]  = useState({});
  const [loading, setLoading] = useState(true);

  const category = searchParams.get('category') || '';
  const search   = searchParams.get('search')   || '';
  const sort     = searchParams.get('sort')     || 'newest';
  const page     = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    api.get('/categories').then(r => setCats(r.data.data || [])).catch(()=>{});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 12, sort });
    if (category) params.set('category', category);
    if (search)   params.set('search', search);
    api.get(`/gems/list?${params}`).then(r => {
      setGems(r.data.data || []);
      setPag(r.data.pagination || {});
    }).finally(() => setLoading(false));
  }, [category, search, sort, page]);

  const setParam = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    p.delete('page');
    setSearchParams(p);
  };

  return (
    <>
      <Helmet><title>Shop Natural Sudath Gemstones | Sudath Gems</title></Helmet>
      <Navbar />
      <div style={{ paddingTop: 72 }}>
        {/* Page header */}
        <div style={{ background:'var(--surface)', borderBottom:'1px solid var(--border)', padding:'60px 0 40px' }}>
          <div className="container">
            <span style={{ fontSize:11, letterSpacing:3, textTransform:'uppercase', color:'var(--gold)', display:'block', marginBottom:12 }}>Sri Lanka</span>
            <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'clamp(2rem,5vw,3.5rem)', fontWeight:300, color:'var(--white)', marginBottom:12 }}>
              {search ? `Results for "${search}"` : category ? categories.find(c=>c.slug===category)?.name || 'Collection' : 'The Collection'}
            </h1>
            <div style={{ width:50, height:1, background:'var(--gold)' }} />
          </div>
        </div>

        <div className="container" style={{ padding:'48px 40px' }}>
          {/* Filters */}
          <div className="shop-filters" style={{ display:'flex', gap:12, marginBottom:40, flexWrap:'wrap', alignItems:'center' }}>
            <button onClick={() => setParam('category', '')} className={`btn btn-sm ${!category ? 'btn-gold' : 'btn-outline'}`}>All Gems</button>
            {categories.map(c => (
              <button key={c.slug} onClick={() => setParam('category', c.slug)} className={`btn btn-sm ${category===c.slug ? 'btn-gold' : 'btn-outline'}`}>{c.name} {c.gem_count > 0 && `(${c.gem_count})`}</button>
            ))}
            <div className="sort-wrap" style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
              <span style={{ fontSize:11, color:'var(--muted)', letterSpacing:1 }}>Sort:</span>
              <select value={sort} onChange={e=>setParam('sort',e.target.value)} className="form-select" style={{ width:'auto', padding:'8px 14px', fontSize:12 }}>
                <option value="newest">Newest</option>
                <option value="price_asc">Price ↑</option>
                <option value="price_desc">Price ↓</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>
          </div>

          {/* Results count */}
          {pagination.total > 0 && (
            <p style={{ fontSize:12, color:'var(--muted)', marginBottom:28, letterSpacing:0.5 }}>
              Showing {(page-1)*12+1}–{Math.min(page*12, pagination.total)} of {pagination.total} gemstones
            </p>
          )}

          {loading ? (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:24 }}>
              {[...Array(8)].map((_,i)=><div key={i} className="skeleton" style={{height:380,borderRadius:8}}/>)}
            </div>
          ) : gems.length > 0 ? (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))', gap:24 }}>
              {gems.map(gem => <GemCard key={gem.id} gem={gem} />)}
            </div>
          ) : (
            <div style={{ textAlign:'center', padding:'80px 0' }}>
              <p style={{ fontFamily:'var(--font-serif)', fontSize:24, color:'var(--muted)', marginBottom:16 }}>No gems found</p>
              <button onClick={()=>setSearchParams({})} className="btn btn-gold">Clear Filters</button>
            </div>
          )}

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="pagination">
              <button className="page-btn" disabled={page<=1} onClick={()=>setParam('page',page-1)}>‹</button>
              {[...Array(pagination.pages)].map((_,i)=>(
                <button key={i} className={`page-btn ${i+1===page?'active':''}`} onClick={()=>setParam('page',i+1)}>{i+1}</button>
              ))}
              <button className="page-btn" disabled={page>=pagination.pages} onClick={()=>setParam('page',page+1)}>›</button>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}
export default ShopPage;
